import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { List, ListItem, Divider, Typography, Paper, Button, Box } from '@mui/material'

import {
  acceptShareAsync,
  declineShareAsync,
  fetchSharesAsync,
  selectPendingSharesToMe,
} from './sharesSlice'
import { fetchPagesAsync } from '../pages/pagesSlice'
import { setFlashAlert, setFlashNotice } from '../ui/uiSlice'
import { showFailedRequest } from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'

// Pages other people have offered me and I haven't answered, at the top of the
// drawer (0.6.27.10, item 5).
//
// A pending share appears nowhere else: it is kept out of /pages.json on purpose,
// so until it is accepted this list is the only place the page is mentioned, and
// the only place to say yes or no to it. It shows nothing at all when there is
// nothing waiting, rather than an empty heading.
//
// Deliberately plain for now, as agreed when the sprint was planned: the point is
// to see it working. The sprint about how the whole app looks will revisit it.
//
// Unlike the share dialog, this can use the flash: the drawer is a persistent
// drawer, not a modal, so nothing covers the message.
// ", shared by jared" when the login is known. Every account has one, so this is
// null only for an unmigrated legacy record (the server sends what it has).
const ownerSuffix = (share) =>
  share.owner_login ? `, shared by ${share.owner_login}` : ''

const classes = {
  item: {
    flexWrap: 'wrap',
  },
  paper: {
    backgroundColor: (theme) => theme.palette.common.white,
    width: '100%',
    padding: 1,
    border: '1px solid rgba(0, 0, 0, 0.12)',
  },
  buttons: {
    display: 'flex',
    gap: 1,
    marginTop: 1,
  },
}

export default function ShareOffers() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const offers = useSelector(selectPendingSharesToMe)
  // The share (and which button) being answered, so its two buttons can't
  // both be pressed and a double click can't send two answers. Keyed by
  // share id + action, not just the share id, since only the button that
  // was actually clicked should show the spinner -- the other one should
  // just be disabled.
  const [answering, submit] = useSubmitting()

  if (offers.length === 0) {
    return null
  }

  // Sends the answer and gets the pages back in step, returning true when it
  // worked. An answer changes which pages I can see, and the groups below this
  // list are built from the pages, not the shares: accepting has to add the page
  // to them, declining has to leave it out.
  const sendAnswer = (share, action, thunk) => submit(async () => {
    const result = await dispatch(thunk(share.id))

    if (result.failure) {
      showFailedRequest(dispatch, result)
      return false
    }

    // The likely reason for a refusal here is that the owner took the offer back
    // between the drawer being drawn and the button being pressed, so refetching
    // clears it out of the list instead of leaving a button that can't work.
    if (!result.response.ok) {
      dispatch(setFlashAlert('Could not answer that offer. It may have been taken back.'))
      dispatch(fetchSharesAsync())
      return false
    }

    await dispatch(fetchPagesAsync())
    return true
  }, `${share.id}-${action}`)

  // Saying yes opens the page, which is what you said yes to. The flash is set
  // and the navigation made in the same run, with no `await` between them, because
  // `clearFlashOnNavigation` keeps a message set by whoever is navigating and
  // clears anything older.
  const handleAccept = async (share) => {
    if (!(await sendAnswer(share, 'accept', acceptShareAsync))) {
      return
    }

    dispatch(
      setFlashNotice(
        `You accepted "${share.page_title}"${ownerSuffix(share)}. ` +
          'It is in your pages under "Shared with me", to read.'
      )
    )
    navigate(`/app/pages/${share.page_id}`)
  }

  // Saying no leaves you where you are: there is nothing to go to.
  const handleDecline = async (share) => {
    if (!(await sendAnswer(share, 'decline', declineShareAsync))) {
      return
    }

    dispatch(
      setFlashNotice(
        `You declined "${share.page_title}"${ownerSuffix(share)}. ` +
          'It stays out of your pages.'
      )
    )
  }

  return (
    <React.Fragment>
      <Divider />
      <List>
        <ListItem>
          <Typography variant='h6'>Waiting for you</Typography>
        </ListItem>
        {offers.map((share) => {
          // This row only -- answering a different offer shouldn't
          // disable this one's buttons.
          const rowBusy = typeof answering === 'string' && answering.startsWith(`${share.id}-`)
          return (
          <ListItem key={`share-offer-${share.id}`} sx={classes.item}>
            <Paper sx={classes.paper} elevation={2}>
              <Typography variant='subtitle1' component='div'>
                {share.page_title}
              </Typography>
              <Typography variant='caption' component='div'>
                {share.owner_login
                  ? `Offered by ${share.owner_login}`
                  : 'Offered to you'}
              </Typography>
              <Box sx={classes.buttons}>
                <Button
                  size='small'
                  variant='contained'
                  disabled={rowBusy}
                  loading={answering === `${share.id}-accept`}
                  onClick={() => handleAccept(share)}
                >
                  Accept
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  disabled={rowBusy}
                  loading={answering === `${share.id}-decline`}
                  onClick={() => handleDecline(share)}
                >
                  Decline
                </Button>
              </Box>
            </Paper>
          </ListItem>
          )
        })}
      </List>
    </React.Fragment>
  )
}
